import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightning } from "@phosphor-icons/react";
import GoogleSignInButton from "../components/GoogleSignInButton";
import fireConfetti from "../lib/confetti";
import useSeo from "../lib/useSeo";

export default function Signup() {
  useSeo({
    title: "Sign up · Start your 14-day Tradelog free trial",
    description: "Sign up free — 14-day trial, no card required. Import broker CSVs, track daily P&L, and pass a real prop firm challenge under strict funded rules.",
    canonical: "/signup",
  });
  const { signup } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(f.name, f.email, f.password);
      // Confetti burst from the button so the first moment on the app feels
      // as polished as the landing page motion.
      try {
        const rect = e.target.querySelector('[data-testid="signup-submit"]')?.getBoundingClientRect();
        fireConfetti(rect ? { x: rect.left + rect.width / 2, y: rect.top } : {});
      } catch {}
      toast.success("14-day free trial activated");
      setTimeout(() => nav("/dashboard"), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 hidden md:flex items-center justify-center bg-secondary/40 border-r border-border p-16">
        <div className="max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <Lightning size={22} weight="fill" className="text-primary" />
            <span className="relative inline-block font-display text-xl font-bold leading-none pt-2">
              <span className="absolute right-0 -top-1 text-[7px] font-semibold tracking-[0.15em] uppercase text-primary/80 whitespace-nowrap">by Yash Khajania</span>
              Tradelog
            </span>
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tight">Start your 14-day free trial.</h1>
          <p className="mt-4 text-muted-foreground">Full access. No credit card.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <h2 className="font-display text-3xl font-bold">Create account</h2>
            <p className="text-sm text-muted-foreground mt-1">Free for 14 days</p>
          </div>
          <GoogleSignInButton label="Sign up with Google" testId="signup-google-btn" />
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="flex-1 h-px bg-border" /> or with email <span className="flex-1 h-px bg-border" />
          </div>
          <div>
            <Label>Name</Label>
            <Input required value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="signup-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" required value={f.email} onChange={e => setF({...f, email: e.target.value})} data-testid="signup-email" />
          </div>
          <div>
            <Label>Password (min 6 chars)</Label>
            <Input type="password" required minLength={6} value={f.password} onChange={e => setF({...f, password: e.target.value})} data-testid="signup-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="signup-submit">
            {loading ? "Creating…" : "Start free trial"}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Already have account? <Link to="/login" className="text-primary underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
